import React, { useContext, useEffect, useReducer, useState, useCallback } from 'react';
import Table from '@paycor/table';
import { isEqual } from 'lodash';
import { Checkbox } from '@paycor/picl/form-elements';
import { ToggleSwitch } from '@paycor/form-elements';
import ToolTip from '@paycor/tooltip';
import { Info, Check } from '@paycor/icon';
import { Colors } from '@paycor/core';
import style from './style.module.scss';
import Button from '@paycor/picl/button';
import {
	getHasNewEmployeeSettingsPageEnabled,
	getHasOnboardingPreventDuplicateFeatureFlag,
	loadCompanyDetails,
	loadEmployeeSettings,
	loadHasBSwiftService,
	loadHasPendingHiresWithEmployeeNumber,
	saveEmployeeSettings
} from 'services/employeeService';
import { employeeConductDialogDisplayNameOptions } from 'constants';
import { scrollToTop } from 'utils/helpers';
import Banner from '@paycor/picl/banner';
import { employeeSettingsActions, employeeSettingsReducer } from 'reducers/employeeSettingsReducer';
import SelectField from 'components/SelectField';
import { UserPrivContext } from 'components/Security/AuthorizeUserPrivilege';
import config from 'config';
import { useParams } from 'react-router-dom';
import { t } from 'i18next';
import { ChangeTypeNotificationModal } from './ChangeTypeNotificationModal';
import LoadingSplashScreen from 'components/LoadingSplashScreen/LoadingSplashScreen';
const BASEURL_HCM = process.env.NODE_ENV === 'production' ? '#{BaseURL-HCM}' : 'https://hcm-quarterly.paycor.com/';

const {
	HOST: { SECURE_BASE_DOMAIN_URL }
} = config;

const initialState = {
	settings: {},
	savedSettings: {},
	errors: {}
};

const DomainType = {
	Processflow: 1,
	CustomFields: 2
};

const GeneralSettings = ({ onIsDirtyChange }) => {
	const { companyId, clientId } = useParams();
	const { hasEditPriv } = useContext(UserPrivContext);

	const [isPageLoading, setIsPageLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);

	const [bannerMessage, setBannerMessage] = useState({
		showBanner: false,
		text: '',
		status: null
	});
	const [_employeeSettings, dispatchEmployeeSettings] = useReducer(employeeSettingsReducer, initialState);
	const [isDirty, setDirty] = useState(false);
	const [hasPreventDuplicateSSNEnabled, setHasPreventDuplicateSSNEnabled] = useState(false);

	const setFeatureFlags = useCallback(async () => {
		const preventDuplicateSSNFlagEnabled = await getHasOnboardingPreventDuplicateFeatureFlag(companyId);
		setHasPreventDuplicateSSNEnabled(preventDuplicateSSNFlagEnabled);
	}, [companyId]);

	useEffect(() => {
		setFeatureFlags();
		const initializeAppEffect = async () => {
			try {
				const hasNewEmployeeSettingsPageEnabled = await getHasNewEmployeeSettingsPageEnabled(companyId);

				if (hasNewEmployeeSettingsPageEnabled) {
					const [companyDetails, employeeSettings, hasPendingHiresWithEmployeeNumber, hasBSwiftService] = await Promise.all([
						loadCompanyDetails(companyId, clientId),
						loadEmployeeSettings(companyId),
						loadHasPendingHiresWithEmployeeNumber(companyId),
						loadHasBSwiftService(companyId, clientId)
					]);

					dispatchEmployeeSettings({
						type: employeeSettingsActions.INITIALIZE,
						payload: {
							value: {
								...employeeSettings,
								employeeNumberSeed: employeeSettings.employeeNumberSeed?.toString(),
								hasPendingHiresWithEmployeeNumber: hasPendingHiresWithEmployeeNumber ?? false,
								hasBSwiftService: hasBSwiftService ?? false,
								companyName: `${companyDetails.companyId} - ${companyDetails.companyName}`
							}
						}
					});
				} else {
					window.location.href = `${SECURE_BASE_DOMAIN_URL}/Perform/Company#${clientId}/HrSettings?useLegacyHrSettings=true`;
				}
			} catch (error) {
				console.log(error);
				setBannerMessage({
					showBanner: true,
					text: t(`Unable_to_load_employee_settings`, `Sorry, we can’t load Employee Settings right now. Please wait a bit and try again.`),
					status: Banner.Status.ERROR
				});
			} finally {
				setIsPageLoading(false);
			}
		};
		initializeAppEffect();
	}, [companyId, clientId, setFeatureFlags]);

	useEffect(
		() => {
			if (bannerMessage.status !== Banner.Status.SUCCESS) return;
			const timer = setTimeout(() => {
				setBannerMessage({
					showBanner: false,
					text: '',
					status: Banner.Status.SUCCESS
				});
			}, 5000);

			return () => {
				clearTimeout(timer);
			};
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[bannerMessage.showBanner]
	);

	useEffect(() => {
		isEqual(_employeeSettings.settings, _employeeSettings.savedSettings) ? setDirty(false) : setDirty(true);
	}, [_employeeSettings]);

	const handleChange = ({ type, key, value }) => {
		dispatchEmployeeSettings({ type, payload: { key, value } });
	};

	const getMasterCheckBoxStatus = () => {
		let allchecked = true;
		_employeeSettings.settings.clientsForRecognitionService.forEach((client) => {
			if (!client.hasRecognitionService) {
				allchecked = false;
			}
		});
		return allchecked;
	};

	// Modified by AI on 08/27/2026. PR 375846 review feedback -- an empty client list must render unchecked, not checked.
	// Modified by AI on 08/27/2026. Edit #2 -- restored the `!clients` guard: live QSB testing proved
	// clientsForOnboardingGuidance genuinely can be undefined (this field's backend deploy hasn't shipped
	// yet), unlike clientsForRecognitionService which is a long-shipped, always-present field.
	const getOnboardingGuidanceMasterCheckBoxStatus = () => {
		const clients = _employeeSettings.settings.clientsForOnboardingGuidance;
		if (!clients || clients.length === 0) {
			return false;
		}
		let allchecked = true;
		clients.forEach((client) => {
			if (!client.hasOnboardingGuidance) {
				allchecked = false;
			}
		});
		return allchecked;
	};

	useEffect(() => {
		onIsDirtyChange(isDirty);
	}, [isDirty, onIsDirtyChange]);

	const handleClientSelect = ({ id, checked }) => {
		let updatedValue;
		if (id === 'master_checkbox') {
			updatedValue = _employeeSettings.settings.clientsForRecognitionService.map((val) => {
				return { ...val, hasRecognitionService: checked };
			});
		} else
			updatedValue = _employeeSettings.settings.clientsForRecognitionService.map((val) => {
				let newObj = { ...val };
				if (newObj.clientId === id) {
					newObj.hasRecognitionService = checked;
				}
				return newObj;
			});
		dispatchEmployeeSettings({
			type: employeeSettingsActions.HANDLE_MASTER_SELECTION,
			payload: { key: 'clientsForRecognitionService', value: updatedValue }
		});
	};

	const handleOnboardingGuidanceClientSelect = ({ id, checked }) => {
		let updatedValue;
		// Modified by AI on 08/27/2026. Edit #2 -- fall back to [] since this field can genuinely be undefined pre-deploy.
		const clientsForOnboardingGuidance = _employeeSettings.settings.clientsForOnboardingGuidance || [];
		if (id === 'onboarding_guidance_master_checkbox') {
			updatedValue = clientsForOnboardingGuidance.map((val) => {
				return { ...val, hasOnboardingGuidance: checked };
			});
		} else
			updatedValue = clientsForOnboardingGuidance.map((val) => {
				let newObj = { ...val };
				// Modified by AI on 08/27/2026. PR 375846 review feedback -- ids are prefixed below to avoid colliding with Recognition's checkbox ids.
				if (`onboarding_guidance_${newObj.clientId}` === id) {
					newObj.hasOnboardingGuidance = checked;
				}
				return newObj;
			});
		dispatchEmployeeSettings({
			type: employeeSettingsActions.HANDLE_MASTER_SELECTION,
			payload: { key: 'clientsForOnboardingGuidance', value: updatedValue }
		});
	};

	const handleChangeTypeSelect = ({ id, checked }) => {
		let isWorkflowTypeActive = true;
		let domainType;
		const updatedValue = _employeeSettings.settings.companyChangeTypes.map((val) => {
			let newObj = { ...val };
			if (newObj.companyChangeTypeId === id) {
				isWorkflowTypeActive = val.isWorkflowTypeActive;
				newObj.active = checked;
				domainType = val.domainType;
			}
			return newObj;
		});

		if (!isWorkflowTypeActive && checked && domainType === DomainType.Processflow) {
			handleShowModal(true);
		}
		dispatchEmployeeSettings({
			type: employeeSettingsActions.HANDLE_MASTER_SELECTION,
			payload: { key: 'companyChangeTypes', value: updatedValue }
		});
	};

	const handleShowModal = (showModal) => {
		setShowModal(showModal);
	};

	const handleSubmit = async () => {
		const { useFirstAvailableEmployeeNumber, employeeNumberSeed } = _employeeSettings.settings;
		scrollToTop();

		if (useFirstAvailableEmployeeNumber && !employeeNumberSeed) {
			let text = (
				<div>
					<div style={{ marginBottom: 10 }}>{t('changes_not_saved', 'Your changes were not saved. Please correct the errors below.')}</div>
					<li>{t('Employee_Number_Seed_Warning Message', 'The starting employee number can be 1 to 999999.')}</li>
				</div>
			);
			setBannerMessage({
				showBanner: true,
				text,
				status: Banner.Status.ERROR
			});
			dispatchEmployeeSettings({
				type: employeeSettingsActions.ERROR,
				payload: { employeeNumberSeed: t('Employee_Number_Seed_Warning Message', 'The starting employee number can be 1 to 999999.') }
			});
			return;
		} else {
			dispatchEmployeeSettings({
				type: employeeSettingsActions.ERROR,
				payload: {}
			});
		}
		setIsPageLoading(true);
		try {
			const response = await saveEmployeeSettings(_employeeSettings.settings);
			setBannerMessage({
				showBanner: true,
				text: t('save_message', 'Success! Your changes were saved.'),
				status: Banner.Status.SUCCESS
			});
			dispatchEmployeeSettings({ type: employeeSettingsActions.INITIALIZE, payload: { value: { ...response, companyName: _employeeSettings.savedSettings.companyName } } });
		} catch (err) {
			console.error(err);
			if (err.name === 'ValidationError') {
				let text = (
					<div>
						<div style={{ marginBottom: 10 }}>{t('changes_not_saved', 'Your changes were not saved. Please correct the errors below.')}</div>
						<li>{err.message._error}</li>
					</div>
				);
				setBannerMessage({
					showBanner: true,
					text,
					status: Banner.Status.ERROR
				});
			} else {
				setBannerMessage({
					showBanner: true,
					text: t('unable_to_save_message', 'Sorry, we can’t save Employee Settings right now. Please wait a bit and try again.'),
					status: Banner.Status.ERROR
				});
			}
		}
		setDirty(false);
		setIsPageLoading(false);
	};

	const handleCancel = () => {
		setDirty(false);
		dispatchEmployeeSettings({ type: employeeSettingsActions.RESET });
	};

	const horizontalSeparator = () => {
		return (
			<>
				<hr className={style.horizontalline} />
			</>
		);
	};

	const navigateToWorkflowLibrary = () => {
		const href = `/ProcessFlow/#/company/${companyId}`;
		window.open(`${BASEURL_HCM}${href}`);
	};

	const renderBanner = () => {
		return (
			<div className={bannerMessage.showBanner ? style.banner_wrapper : ''}>
				<Banner
					id={'banner'}
					message={bannerMessage.showBanner ? bannerMessage.text : null}
					status={bannerMessage.status ? bannerMessage.status : Banner.Status.SUCCESS}
					show={bannerMessage.showBanner}
					onClose={() => {
						setBannerMessage({ ...bannerMessage, showBanner: !bannerMessage.showBanner });
					}}
					onToggle={() => {
						setBannerMessage({ ...bannerMessage, showBanner: !bannerMessage.showBanner });
					}}
				/>
			</div>
		);
	};

	const renderEmployeeConduct = () => {
		return (
			<>
				<section className={style.section_container}>
					<span className={style.section_title}>{t('employee_conduct', 'Employee Conduct')}</span>
					<span className={style.employee_conduct_title}>{t('Select_which_term_you_prefer_for_employee_conduct', 'Select which term you prefer for employee conduct.')}</span>
					<SelectField
						id={'employeeConductDialogDisplayNameOptions'}
						isRequired={true}
						displayOptions={employeeConductDialogDisplayNameOptions}
						value={_employeeSettings.settings.employeeConductDialogDisplayNameValue}
						onHandleChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'employeeConductDialogDisplayNameValue', value: e.value })}
					/>
				</section>
				{horizontalSeparator()}
			</>
		);
	};

	const renderEmployeeRecognition = () => {
		return (
			<>
				<section className={style.section_container}>
					<span className={style.section_title}>{t('employee_recognition', 'Employee Recognition')}</span>
					<div className={style.tooltip_wrapper}>
						<span>{t('Select_which_clients_should_have_Recognition_enabled', 'Select which clients should have Recognition enabled')}</span>
						<ToolTip position={ToolTip.Positions.TOP}>
							<ToolTip.Anchor>
								<Info color={Colors.BLUE2} />
							</ToolTip.Anchor>
							<ToolTip.Body>
								<span className={style.message_width}>{t('Allow_employees', 'Allow employees to recognize one another inside the desktop and the mobile apps')}</span>
							</ToolTip.Body>
						</ToolTip>
					</div>
					<div className={style.table_wrapper}>
						<Table isSelectable={false} isStriped={true} isResizable={false} fullBordered={true}>
							<Table.Header columnWidth="3fr">
								<Table.HeaderCell id={'master_checkbox_cell'}>
									<span className={style.cell_vertical_align}>
										<Checkbox
											id={'master_checkbox'}
											checkedStatus={getMasterCheckBoxStatus() ? Checkbox.StatusTypes.CHECKED : Checkbox.StatusTypes.NONE}
											onChange={(e) => handleClientSelect(e)}
										/>
									</span>
								</Table.HeaderCell>
								<Table.HeaderCell id={'client'} fixedHeight={'10px'} columnWidth="18fr">
									<span className={style.cell_vertical_align}>{t('client', 'Client')}</span>
								</Table.HeaderCell>
							</Table.Header>
							<Table.Body>
								{_employeeSettings.settings.clientsForRecognitionService.map((client, idx) => (
									<Table.Row id={`client_row_${idx}`} key={idx}>
										<Table.Cell id={`employee_checkbox_${idx}`}>
											<span className={style.cell_vertical_align} data-qa={client.hasRecognitionService ? 'checked' : 'unchecked'}>
												<Checkbox
													id={client.clientId}
													checkedStatus={client.hasRecognitionService ? Checkbox.StatusTypes.CHECKED : Checkbox.StatusTypes.NONE}
													onChange={(e) => handleClientSelect(e)}
												/>
											</span>
										</Table.Cell>
										<Table.Cell id={`client_${idx}`}>
											<span className={style.cell_vertical_align}>{client.clientDisplayName}</span>
										</Table.Cell>
									</Table.Row>
								))}
							</Table.Body>
						</Table>
					</div>
				</section>
				{horizontalSeparator()}
			</>
		);
	};

	const renderOnboardingGuidance = () => {
		return (
			<>
				<section className={style.section_container}>
					<span className={style.section_title}>{t('onboarding_guidance', 'Onboarding Guidance')}</span>
					<div className={style.tooltip_wrapper}>
						<span>{t('Select_which_clients_should_have_Onboarding_Guidance_enabled', 'Select which clients should have Onboarding Guidance enabled')}</span>
						<ToolTip position={ToolTip.Positions.TOP}>
							<ToolTip.Anchor>
								<Info color={Colors.BLUE2} />
							</ToolTip.Anchor>
							<ToolTip.Body>
								<span className={style.message_width}>
									{t('Show_new_hire_guidance', 'Show admins guidance about state and local hiring requirements during the new hire wizard')}
								</span>
							</ToolTip.Body>
						</ToolTip>
					</div>
					<div className={style.table_wrapper}>
						<Table isSelectable={false} isStriped={true} isResizable={false} fullBordered={true}>
							<Table.Header columnWidth="3fr">
								<Table.HeaderCell id={'onboarding_guidance_master_checkbox_cell'}>
									<span className={style.cell_vertical_align}>
										<Checkbox
											id={'onboarding_guidance_master_checkbox'}
											checkedStatus={getOnboardingGuidanceMasterCheckBoxStatus() ? Checkbox.StatusTypes.CHECKED : Checkbox.StatusTypes.NONE}
											onChange={(e) => handleOnboardingGuidanceClientSelect(e)}
										/>
									</span>
								</Table.HeaderCell>
								<Table.HeaderCell id={'onboarding_guidance_client'} fixedHeight={'10px'} columnWidth="18fr">
									<span className={style.cell_vertical_align}>{t('client', 'Client')}</span>
								</Table.HeaderCell>
							</Table.Header>
							<Table.Body>
								{/* Modified by AI on 08/27/2026. Edit #2 -- fall back to [] since this field can genuinely be undefined pre-deploy. */}
								{(_employeeSettings.settings.clientsForOnboardingGuidance || []).map((client, idx) => (
									<Table.Row id={`onboarding_guidance_client_row_${idx}`} key={idx}>
										<Table.Cell id={`onboarding_guidance_checkbox_${idx}`}>
											<span className={style.cell_vertical_align} data-qa={client.hasOnboardingGuidance ? 'checked' : 'unchecked'}>
												<Checkbox
													id={`onboarding_guidance_${client.clientId}`}
													checkedStatus={client.hasOnboardingGuidance ? Checkbox.StatusTypes.CHECKED : Checkbox.StatusTypes.NONE}
													onChange={(e) => handleOnboardingGuidanceClientSelect(e)}
												/>
											</span>
										</Table.Cell>
										<Table.Cell id={`onboarding_guidance_client_${idx}`}>
											<span className={style.cell_vertical_align}>{client.clientDisplayName}</span>
										</Table.Cell>
									</Table.Row>
								))}
							</Table.Body>
						</Table>
					</div>
				</section>
				{horizontalSeparator()}
			</>
		);
	};

	const renderEmployeeSelfServiceAccess = () => {
		return (
			<>
				<section className={style.section_container}>
					<span className={style.section_title}>{t('employee_self_service_access', 'Employee Self-Service Access')}</span>
					<div className={style.tooltip_wrapper} style={{ flexDirection: 'column', gap: '8px' }}>
						<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<span>
								<strong>
									{t(
										'Employees_will_be_able_to_make_changes_to_their_own_data',
										'Employees will be able to make changes to their own data and, if activated, the associated workflows will be completed based upon the approval steps you have defined.'
									)}
								</strong>
							</span>
							<ToolTip position={ToolTip.Positions.TOP}>
								<ToolTip.Anchor>
									<Info color={Colors.BLUE2} />
								</ToolTip.Anchor>
								<ToolTip.Body>
									<span className={style.message_width}>
										{t('When_change_types_are_selected', 'When change types are selected, employees will be able to make changes to their own data')}
									</span>
								</ToolTip.Body>
							</ToolTip>
						</div>
						<span>
							<strong>{`${t('Note', 'Note')}: `}</strong>
							{t('Scroll_within_the_table', 'Scroll within the table below to view additional rows')}
						</span>
					</div>
					<div className={style.table_wrapper}>
						<Table isSelectable={false} isStriped={true} isResizable={false} fullBordered={true} fixedHeight={300}>
							<Table.Header columnWidth="3fr">
								<Table.HeaderCell id={'changeType'} fixedHeight={'10px'} columnWidth="18fr">
									<span className={style.cell_vertical_align}>{t('change_type', 'Change Type')}</span>
								</Table.HeaderCell>
								<Table.HeaderCell id={'employee'} columnWidth="2fr">
									<span className={style.cell_vertical_align}>{t('employee', 'Employee')}</span>
								</Table.HeaderCell>
							</Table.Header>
							<Table.Body>
								{_employeeSettings.settings.companyChangeTypes.map((changeType, idx) => (
									<Table.Row id={`change_type_row_${idx}`} key={idx}>
										<Table.Cell id={`change_type_${idx}`}>
											<span className={style.cell_vertical_align}>
												{changeType.changeTypeName}
												{changeType.isDisabled && (
													<div>
														<span className={style.tableInfo}>
															<Info color={Colors.BLUE2} />
														</span>
														<span>
															{t('disabledChangeTypeTooltipMessage', 'To enable this Change Type, please turn ON the associated workflow within the ')}
															<span onClick={navigateToWorkflowLibrary} className={style.link}>
																{t('workflowLibrary', 'Workflow Library.')}
															</span>
														</span>
													</div>
												)}
											</span>
										</Table.Cell>
										<Table.Cell id={`change_type_checkbox_${idx}`} className={style.table_checkbox}>
											<span className={style.cell_vertical_align}>
												<Checkbox
													id={changeType.companyChangeTypeId}
													checkedStatus={changeType.active ? Checkbox.StatusTypes.CHECKED : Checkbox.StatusTypes.NONE}
													onChange={(e) => handleChangeTypeSelect(e)}
													isDisabled={changeType.isDisabled}
												/>
											</span>
										</Table.Cell>
									</Table.Row>
								))}
							</Table.Body>
						</Table>
					</div>
				</section>
				{horizontalSeparator()}
			</>
		);
	};

	const renderEmployeePersonalInfo = () => {
		return (
			<>
				<section className={style.section_container}>
					<span className={style.section_title}>{t('Employee_Personal_Information', 'Employee Personal Information')}</span>
					<span className={style.toggle_container}>
						<ToggleSwitch
							toggleState={_employeeSettings.settings.useGenderIdentityOption}
							onLabel={'On'}
							offLabel={'Off'}
							onChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'useGenderIdentityOption', value: e })}
						/>
						<span>
							{t('sex_identity_option_off', 'When off, employees will only be able to select their Sex from two options: Male or Female')}
							<br />
							{t(
								'sex_identity_option_on',
								'When on, in addition to their Sex, employees will be able to select their Gender Identity from four gender options: Male, Female, Non-Binary, Other'
							)}{' '}
						</span>
					</span>
					<span className={style.toggle_container}>
						<ToggleSwitch
							toggleState={_employeeSettings.settings.allowEmployeePictureEdit}
							onLabel={'On'}
							offLabel={'Off'}
							onChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'allowEmployeePictureEdit', value: e })}
						/>
						<p>{t('employee_picture_edit', 'Allow employees to edit their profile photo')} </p>
					</span>
				</section>
				{horizontalSeparator()}
			</>
		);
	};

	const renderEmployeeImmunization = () => {
		return (
			<>
				<section className={style.section_container}>
					<span className={style.section_title}>{t('employee_immunizations', 'Employee Immunizations')}</span>
					<span className={style.toggle_container}>
						<ToggleSwitch
							toggleState={_employeeSettings.settings.allowEmployeeImmunizations}
							onLabel={'On'}
							offLabel={'Off'}
							onChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'allowEmployeeImmunizations', value: e })}
						/>
						<span>{t('employee_immunization_data', 'Gather and report on Employee Immunization data')}</span>
					</span>
					<>
						{_employeeSettings.settings.allowEmployeeImmunizations && (
							<div className={style.checkbox_container}>
								<Checkbox
									checkedStatus={_employeeSettings.settings.immunizationExemptionDocumentationRequired ? Checkbox.StatusTypes.CHECKED : Checkbox.StatusTypes.NONE}
									onChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'immunizationExemptionDocumentationRequired', value: e.checked })}
									label={t('Require_supporting_documentation_for_exemptions', 'Require supporting documentation for exemptions')}
								/>
								<span>
									<strong className={style.left_gap}>{t('disclaimar', 'Disclaimer')}:</strong>{' '}
									{t('disclaimar_note', 'Additional legal obligations, such as disability information, may be required to support immunization exemptions.')}
								</span>
							</div>
						)}
					</>
				</section>
				{horizontalSeparator()}
			</>
		);
	};

	const renderEmployeeTerminationWizard = () => {
		return (
			<>
				<section className={style.section_container}>
					<span className={style.section_title}>{t('Enhanced_Termination_Wizard', 'Enhanced Termination Wizard')}</span>
					<div className={style.tooltip_wrapper}>
						<span>
							{t('termination_wizard_desc', 'Enhanced Termination Wizard allows for features such as Reassigning Managers, Draft Job Openings, and Collecting Custom Fields')}
						</span>
						<ToolTip position={ToolTip.Positions.TOP}>
							<ToolTip.Anchor>
								<Info color={Colors.BLUE2} />
							</ToolTip.Anchor>
							<ToolTip.Body>
								<div className={style.message_width}>
									{t('termination_wizard_desc_tooltip_message', `To include custom fields, visit Configure Company > Custom Fields and select 'Show in Termination Wizard'.`)}
								</div>
							</ToolTip.Body>
						</ToolTip>
					</div>
					<span className={style.toggle_container}>
						<ToggleSwitch
							toggleState={_employeeSettings.settings.useEnhancedTerminationWizard}
							onLabel={'On'}
							offLabel={'Off'}
							isDisabled={_employeeSettings.settings.disableSettingForTerminationWizard}
							onChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'useEnhancedTerminationWizard', value: e })}
						/>
						<p>{t('Use_Enhanced_Termination_Wizard', 'Use Enhanced Termination Wizard')}</p>
					</span>
				</section>
				{horizontalSeparator()}
			</>
		);
	};

	const renderDuplicateEmployeeRecords = () => {
		return (
			<section className={style.section_container}>
				<span className={style.section_title}>{t('Duplicate_Employee_Records', 'Duplicate Employee Records')}</span>
				{renderShowDuplicateSSNWarningToggle()}
				{hasPreventDuplicateSSNEnabled && renderPreventHiringDuplicateSSN()}
				{horizontalSeparator()}
			</section>
		);
	};

	const renderShowDuplicateSSNWarningToggle = () => {
		return (
			<div className={style.toggle_container}>
				<ToggleSwitch
					toggleState={_employeeSettings.settings.allowDupEERecordsEdit}
					onLabel={'On'}
					offLabel={'Off'}
					onChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'allowDupEERecordsEdit', value: e })}
				/>
				<span>{t('Duplicate_Employee_Records_toggle', 'Show a warning message when a duplicate Social Security Number exists during hiring')}</span>
				<ToolTip position={ToolTip.Positions.TOP}>
					<ToolTip.Anchor>
						<Info color={Colors.BLUE2} />
					</ToolTip.Anchor>
					<ToolTip.Body>
						<div className={style.message_width}>
							{t(
								'Duplicate_Employee_Records_toggle_tooltip_message',
								"A warning message indicates a duplicate SSN already exists, and the user doesn't have access; they should contact their HR Admin."
							)}
						</div>
					</ToolTip.Body>
				</ToolTip>
			</div>
		);
	};

	const renderPreventHiringDuplicateSSN = () => {
		return (
			<div className={style.toggle_container}>
				<ToggleSwitch
					toggleState={_employeeSettings.settings.preventHiringDuplicateSSN}
					onLabel={'On'}
					offLabel={'Off'}
					onChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'preventHiringDuplicateSSN', value: e })}
				/>
				<span>{t('Prevent_Hiring_Duplicate_SSN_toggle', 'Prevent duplicate Social Security Numbers for select imports and during the hiring process.')}</span>
				<ToolTip position={ToolTip.Positions.TOP}>
						<ToolTip.Anchor>
							<Info color={Colors.BLUE2} />
						</ToolTip.Anchor>
						<ToolTip.Body>
							<div className={style.message_width}>
								{t('Prevent_Hiring_Duplicate_SSN_Tooltip_Message', 'This setting only applies to Employee Import, Onboarding Invite, and Manual Hire.n')}
							</div>
						</ToolTip.Body>
					</ToolTip>
			</div>
		);
	};

	const renderEmployeeViewPayRates = () => {
		if (!_employeeSettings.settings.showEmployeePayRatesSetting) return null;

		return (
			<section className={style.section_container}>
				<span className={style.section_title}>{t('Employee_Pay_Rates', 'Employee Pay Rates')}</span>
				<span className={style.description}>
					<span>{t('Employee_Pay_Rates_Desc', 'The Pay Rates page shows each employee their current pay rate information and history.')}</span>
				</span>
				<div className={style.toggle_container}>
					<ToggleSwitch
						toggleState={_employeeSettings.settings.changeCompanyEmployeesViewPayrates}
						onLabel={'On'}
						offLabel={'Off'}
						onChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'changeCompanyEmployeesViewPayrates', value: e })}
					/>
					<span>{t('Employee_Pay_Rates_toggle', 'Allow employees to see the Pay Rates page.')}</span>
				</div>
				<div className={style.toggle_container}>
					<ToggleSwitch
						toggleState={_employeeSettings.settings.allowEmployeeViewPayRangeMetrics}
						onLabel={'On'}
						offLabel={'Off'}
						onChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'allowEmployeeViewPayRangeMetrics', value: e })}
					/>
					<span>
						{t('Enable_PayRange_Heading_Text', 'Allow employees to see Compa-Ratio, Range Penetration, and Pay Range (Min, Mid, and Max) information on the Pay Rates page')}
					</span>
					<ToolTip position={ToolTip.Positions.TOP}>
						<ToolTip.Anchor>
							<Info color={Colors.BLUE2} />
						</ToolTip.Anchor>
						<ToolTip.Body>
							<div className={style.message_width}>
								{t('PayRange_Tooltip_Text', 'Employees can use this information to compare their pay rates to the pay range for their position')}
							</div>
						</ToolTip.Body>
					</ToolTip>
				</div>
			</section>
		);
	};

	const renderButtons = () => {
		function hasFormValuesChanged(o1, o2) {
			for (var p1 in o1) {
				if (o1.hasOwnProperty(p1)) {
					if (o1[p1] !== o2[p1]) {
						return false;
					}
				}
			}
			for (var p2 in o2) {
				if (o2.hasOwnProperty(p2)) {
					if (o1[p2] !== o2[p2]) {
						return false;
					}
				}
			}
			return true;
		}

		return (
			<section className={style.sticky_buttons}>
				<div>
					<hr className={style.Footerhorizontalline} />
					<Button type={Button.Types.SECONDARY} onClick={handleCancel}>
						{t('cancel', 'Cancel')}
					</Button>
					<Button
						type={Button.Types.SAVE}
						onClick={handleSubmit}
						actionType="submit"
						icon={Check}
						isDisabled={hasFormValuesChanged(_employeeSettings.settings, _employeeSettings.savedSettings) || !hasEditPriv}
					>
						{t('save', 'Save')}
					</Button>
				</div>
			</section>
		);
	};

	const renderModal = () => <ChangeTypeNotificationModal handleShowModal={handleShowModal} showModal={showModal} />;

	return (
		<>
			{isPageLoading && <LoadingSplashScreen pageLoad={true} />}
			{Object.keys(_employeeSettings.settings).length > 0 && (
				<article className={style.general_setting_content}>
					{renderBanner()}
					{renderEmployeeConduct()}
					{renderEmployeeRecognition()}
					{renderOnboardingGuidance()}
					{renderEmployeeSelfServiceAccess()}
					{renderEmployeePersonalInfo()}
					{renderEmployeeImmunization()}
					{renderEmployeeTerminationWizard()}
					{renderDuplicateEmployeeRecords()}
					{renderEmployeeViewPayRates()}
					{renderButtons()}
					{renderModal()}
				</article>
			)}
		</>
	);
};
export default GeneralSettings;
